// Офлайн-черга відміток водія: зберігається в localStorage, надсилається, щойно зʼявиться мережа.
// client_ref робить надсилання ідемпотентним: повтор не створює другого кроку (сервер відповідає duplicate).
export type Mark = {
  client_ref: string;
  trip_id: string;
  step_key: string;
  at: string;
  photos: string[];
  note?: string;
};

const KEY = 'xq.tg.marks';

export function load(): Mark[] {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Mark[]) : [];
  } catch {
    return [];
  }
}

function save(items: Mark[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    /* сховище переповнене або недоступне — відмітка лишається лише в памʼяті */
  }
}

export function enqueue(m: Mark): Mark[] {
  const items = load().filter((x) => x.client_ref !== m.client_ref);
  items.push(m);
  save(items);
  return items;
}

export function newRef(): string {
  const rnd = crypto.getRandomValues(new Uint8Array(12));
  return 'm' + Array.from(rnd, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Надсилає по черзі; помилка мережі зупиняє (спробуємо пізніше), відповідь сервера 4xx знімає відмітку з черги. */
export async function flush(send: (m: Mark) => Promise<void>): Promise<{ sent: number; left: number }> {
  let sent = 0;
  for (const m of load()) {
    try {
      await send(m);
      sent += 1;
      save(load().filter((x) => x.client_ref !== m.client_ref));
    } catch (e) {
      const status = (e as { status?: number }).status;
      if (status && status >= 400 && status < 500) {
        save(load().filter((x) => x.client_ref !== m.client_ref));
        continue;
      }
      break;
    }
  }
  return { sent, left: load().length };
}

/** Фото з камери → JPEG ≤ 1280 px (менше трафіку і місця в черзі). */
export async function compress(file: File, max = 1280, quality = 0.72): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((ok, fail) => {
      const i = new Image();
      i.onload = () => ok(i);
      i.onerror = fail;
      i.src = url;
    });
    const k = Math.min(1, max / Math.max(img.width, img.height));
    const c = document.createElement('canvas');
    c.width = Math.round(img.width * k);
    c.height = Math.round(img.height * k);
    c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
    return c.toDataURL('image/jpeg', quality);
  } finally {
    URL.revokeObjectURL(url);
  }
}
